import React, { useState, useMemo, useEffect, useRef } from "react";
import Modal from "../Modal";
import styled from '@emotion/styled';
import { isMobile } from 'react-device-detect';
import { TabbedCard } from "../Card";
import { DynamicTabController } from "../../TabController/TabController";
import { TabPanel } from "../../TabController/TabPanel";
import SelectWithLabel from "../../Inputs/Select";
import DatePicker from "../../Inputs/DatePicker";
import Schedule from "../Scheduler";
import EyeTicket from "../EyeTicket/EyeTicket";
import { TabContent } from "../../StyledComponents/Tabs/Tabs";
import { FieldContainer, Row } from "../../StyledComponents/Forms/Forms";
import { CancelButton } from "../../StyledComponents/Buttons/Buttons";
import { formatDateToDDMMYYYY, extractHHMM, mergeDateWithTime, combineDateAndTimeUTC } from "../../Helpers/dateFns";
import { fetchFavouriteOpticians } from "../../../http/optician";
import { updateClinic, createClinic, fetchClinicSchedule, fetchClinicScheduleRestrictions, removeClinicRestriction, restrictClinicSchedule, removeClinicEyeTicket } from "../../../http/clinics";
import { fetchLinkedAccounts, getUsersEmailAddress } from "../../../http/account";
import { Alert } from "../Alert";
import { AddPerson } from "../../../assets/icons/AddPerson";
import OpticiansModal from "../Opticians/Opticians";
import { fetchEyeTicketById } from "../../../http/eyeticket";
import { StyledModal } from "../../StyledComponents/Modals/Modals";

const ModalFooter = styled.div`
  text-align: right;
  flex-shrink: 0;
  padding: 10px 0px;
`;

const Heading = styled.h3`
  margin: 0;
  margin-top: 10px;
  padding-bottom: 5px;
`;

const Paragraph = styled.p`
  margin: 0 0 20px 0;
  font-size:15px;
`;

const Button = styled.button`
  background-color: #93C572;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  padding: 10px 20px;
  margin-right: 10px;

  &:hover {
    background-color: #698D51;
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const ScheduleWrapper = styled.div`
  max-height: 400px;
  overflow-y: auto;
  width:100%;
`;

const defaultClinic = {
  enduser_email: "",
  optician: "",
  companyname: "",
  clinicdate: "",
  starttime: "",
  startTimeFrm: "",
  endtime: "",
  appointment_duration: 15,
  datecreated: "",
  createdby: "",
  openClinic: 0
}

export default function Clinic({
  showHideModal,
  isModalOpen,
  colours,
  clinic = defaultClinic,
  isNewClinic = true
}) {
  const [selectedEyeTicket, setSelectedEyeTicket] = useState({});
  const [clinicSchedule, setClinicSchedule] = useState([]);
  const [showOpticians, setShowOpticians] = useState(false);
  const [showEyeTicket, setShowEyeTicket] = React.useState(false);
  const [usersEmail, setUsersEmail] = React.useState();
  const [warningMessage, setWarningMessage] = React.useState();
  const [clinicDetails, setClinicDetails] = useState(clinic);
  const [favouriteOpticians, setFavouriteOpticians] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [missingFields, setMissingFields] = useState({});
  const [linkedAccounts, setLinkedAccounts] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [isEyeTicketLoading, setIsEyeTicketLoading] = useState(false);
  const [newEyeTicketId, setNewEyeTicketId] = useState(null);
  const [selectedScheduleData, setSelectedScheduleData] = useState(null);
  const [scheduleInUse, setScheduleInUse] = useState(false);
  const actionInProgressRef = useRef(false);

  useEffect(() => {
    const checkScheduleInUse = async () => {
      const inUse = await isScheduleInUse();
      setScheduleInUse(inUse);
    };

    checkScheduleInUse();
  }, [clinicSchedule]);

  const isScheduleInUse = async () => {
    return clinicSchedule?.some(x => x.IsBlocked === 1);
  };

  const setDefaultValues = () => {
    if (isNewClinic) {
      const d = new Date();
      let stringDate = d.toISOString();

      const initialValues = {
        accountid: linkedAccounts[0].value,
        enduser_email: usersEmail,
        OpticianID: "",
        companyname: linkedAccounts[0].label,
        clinicdate: stringDate,
        starttime: "09:00",
        endtime: "17:00",
        appointment_duration: 20,
        datecreated: stringDate,
        createdby: usersEmail,
        OpenClinic: 0
      };

      setClinicDetails(prevState => ({ ...prevState, ...initialValues }));
    }
  };

  const handleSaveChanges = async () => {
    const isValid = validateForm();
    if (!isValid) {
      return;
    }

    setIsLoading(true);
    try {
      const clinicDate = new Date(clinicDetails.clinicdate);
      clinicDate.setUTCHours(0, 0, 0, 0);

      const formattedClinicDetails = {
        unqid: clinicDetails.unqid,
        accountid: clinicDetails.accountid,
        enduser_email: clinicDetails.enduser_email,
        OpticianID: clinicDetails.OpticianID,
        companyname: clinicDetails.companyname,
        clinicdate: clinicDate.toISOString(),
        starttime: combineDateAndTimeUTC(clinicDate, extractHHMM(clinicDetails.starttime, true)),
        endtime: combineDateAndTimeUTC(clinicDate, extractHHMM(clinicDetails.endtime, true)),
        appointment_duration: clinicDetails.appointment_duration,
        datecreated: clinicDetails.datecreated,
        createdby: clinicDetails.createdby,
        OpenClinic: parseInt(clinicDetails.OpenClinic)
      };

      if (isNewClinic) {
        await createClinic(formattedClinicDetails);
      } else {
        await updateClinic(formattedClinicDetails);
      }

      setError(null);
      showHideModal(true);
    } catch (e) {
      setError(e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isLoading && linkedAccounts.length > 0 && usersEmail) {
      setDefaultValues();
    }
  }, [linkedAccounts, usersEmail]);

  const validateForm = () => {
    let newMissingFields = {};
    let isValid = true;

    if (!clinicDetails.clinicdate) newMissingFields.clinicdate = true;
    if (!clinicDetails.OpticianID || clinicDetails.OpticianID === 'Select an optician..') newMissingFields.OpticianID = true;
    if (!clinicDetails.starttime) newMissingFields.starttime = true;

    if (!clinicDetails.endtime) newMissingFields.endtime = true;
    if (!clinicDetails.appointment_duration) newMissingFields.appointment_duration = true;

    if (clinicDetails.starttime && clinicDetails.endtime) {
      if (extractHHMM(clinicDetails.endtime, true) < extractHHMM(clinicDetails.starttime, true)) {
        setWarningMessage("Ensure that the end time is greater than the start time.");
        newMissingFields.endtime = true;
      }
    } else {
      setWarningMessage(null)
    }

    for (let key in newMissingFields) {
      if (newMissingFields[key] === true) {
        isValid = false;
        break;
      }
    }

    setMissingFields(newMissingFields);
    return isValid;
  };

  let isNewEyeTicket = false;

  function ShowHideEyeTicket(isNewTicket = true, refresh = false, eyeTicketId = 0) {
    setShowEyeTicket(!showEyeTicket);

    if (!showEyeTicket) {
      setIsEyeTicketLoading(true);
      isNewEyeTicket = isNewTicket;
      if (eyeTicketId !== 0) {
        getEyeTicketForEditing(eyeTicketId);
      } else {
        setSelectedEyeTicket({});
        setIsEyeTicketLoading(false);
      }
    }

    if (refresh) {
      loadClinicSchedule();
    }
  }

  const times = useMemo(() => {
    const slots = [];
    const totalMinutesInADay = 24 * 60;

    if (clinicDetails?.appointment_duration) {
      for (let minute = 0; minute < totalMinutesInADay; minute += parseInt(clinicDetails?.appointment_duration, 10)) {
        const hours = Math.floor(minute / 60);
        const minutes = minute % 60;

        const formattedHours = hours.toString().padStart(2, '0');
        const formattedMinutes = minutes.toString().padStart(2, '0');

        slots.push({ label: `${formattedHours}:${formattedMinutes}`, value: `${formattedHours}:${formattedMinutes}` });
      }

      return slots;
    } else { return null; }
  }, [clinicDetails?.appointment_duration]);

  const getEyeTicketForEditing = async (id) => {
    setIsLoading(true);
    try {
      const eyeTicket = await fetchEyeTicketById(id);
      setSelectedEyeTicket(eyeTicket);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setIsEyeTicketLoading(false);
      setIsLoading(false);
    }
  };

  const loadFavouriteOpticians = async () => {
    setIsLoading(true);
    try {
      const opticians = await fetchFavouriteOpticians();
      const favouriteOpticians = [];

      favouriteOpticians.push({ label: "Select an optician..", value: null });

      opticians.forEach(optician => {
        favouriteOpticians.push({ label: optician.companyname, value: optician.unqID });
      });

      setFavouriteOpticians(favouriteOpticians);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setIsLoading(false);
    }
  };

  const loadClinicSchedule = async () => {
    if (!isNewClinic && clinicDetails?.unqid) {
      try {
        const clinicSchedules = await fetchClinicSchedule(clinicDetails.unqid, clinicDetails.clinicdate);
        const clinicRestrictions = await fetchClinicScheduleRestrictions(clinicDetails.unqid);

        const combinedSchedules = clinicSchedules.map(schedule => {
          const scheduleTime = new Date(schedule.ScheduledAppointmentDateTime);

          const restriction = clinicRestrictions.find(restriction => {
            const startTime = new Date(restriction.starttime);
            const endTime = new Date(restriction.endtime);
            return scheduleTime >= startTime && scheduleTime <= endTime;
          });

          if (restriction) {
            return {
              ...schedule,
              restrictionsId: restriction.unqid
            };
          }
          return schedule;
        });

        setClinicSchedule(combinedSchedules);
        setError(null);
      } catch (e) {
        setError(e);
      }
    }
  };
  useEffect(() => {
    const loadLinkedAccounts = async () => {
      setIsLoading(true);
      try {
        const accounts = await fetchLinkedAccounts();
        const userAccounts = [];

        accounts.forEach(account => {
          userAccounts.push({ label: account.accountdescrip, value: account.accountid });
        });

        setLinkedAccounts(userAccounts);
        setError(null);
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    };

    const getUsersEmail = async () => {
      setIsLoading(true);
      try {
        const email = await getUsersEmailAddress();

        setUsersEmail(email);
        setError(null);
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    };

    loadFavouriteOpticians();
    loadLinkedAccounts();
    getUsersEmail();
    loadClinicSchedule();
  }, []);

  const handleChange = (field, value) => {
    setClinicDetails(prev => ({ ...prev, [field]: value }));
  };

  const yesNo = [
    { label: 'No', value: 0 },
    { label: 'Yes', value: 1 }
  ];

  const appointmentDurations = [
    { label: '5', value: 5 },
    { label: '10', value: 10 },
    { label: '15', value: 15 },
    { label: '20', value: 20 },
    { label: '25', value: 25 },
    { label: '30', value: 30 },
  ];

  function ShowHideOptician() {
    setShowOpticians(!showOpticians);
    loadFavouriteOpticians();
  }

  const handleRemoveEyeTicket = async (eyeTicketId) => {
    setIsLoading(true);
    try {
      await removeClinicEyeTicket(clinicDetails.unqid, eyeTicketId);
      await loadClinicSchedule();
    } catch (e) {
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }

  const handleRemoveRestriction = async (restrictionId) => {
    setIsLoading(true);
    try {
      await removeClinicRestriction(restrictionId);
      await loadClinicSchedule();
    } catch (e) {
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }

  const combineTimeAndDate = (time) => {
    const clinicDate = new Date(clinicDetails.clinicdate);
    clinicDate.setUTCHours(0, 0, 0, 0);

    const startDateTime = combineDateAndTimeUTC(clinicDate, extractHHMM(time, true));

    return startDateTime;
  }

  const handleAddRestriction = async (startTime) => {
    if (isLoading || actionInProgressRef.current) return;
    actionInProgressRef.current = true;
    setIsLoading(true);

    try {
      const clinicDate = new Date(clinicDetails.clinicdate);
      clinicDate.setUTCHours(0, 0, 0, 0);

      const durationMinutes = clinicDetails.appointment_duration;
      const startDateTime = combineDateAndTimeUTC(clinicDate, extractHHMM(startTime, true));

      let endDateTime = new Date(startDateTime);
      endDateTime = new Date(endDateTime.getTime() + (durationMinutes - 1) * 60000);

      const restriction = {
        reason: "Restricted",
        starttime: startDateTime,
        endtime: endDateTime.toISOString(),
        clinicId: clinicDetails?.unqid
      };

      await restrictClinicSchedule(restriction);
      await loadClinicSchedule();
    } catch (e) {
      setError(e);
    } finally {
      setIsLoading(false);
      actionInProgressRef.current = false;
    }
  }

  return (
    <>
      {showEyeTicket && !isEyeTicketLoading &&
        <EyeTicket
          ticket={selectedEyeTicket}
          isNewEyeTicket={isNewEyeTicket}
          showHideModal={ShowHideEyeTicket}
          isModalOpen={showEyeTicket}
          email={usersEmail}
          colours={colours}
          setNewEyeTicketId={setNewEyeTicketId}
          clinicId={clinicDetails.unqid.toString()}
          clinicAppointment={combineTimeAndDate(selectedScheduleData)}
          opticianEmail={clinicDetails.OpticianID}
        />
      }
      {showOpticians &&
        <OpticiansModal
          showHideModal={ShowHideOptician}
          isModalOpen={showOpticians}
          colours={colours}
          email={usersEmail}
          accountCode={clinicDetails.accountid}
          companyName={clinicDetails.companyname} />}
      <Modal open={isModalOpen}>
        <StyledModal>
          <TabbedCard
            label={'Clinic Management'}
            headercolour={colours.sidebar}
            headerTextColour={colours.font}
            shadow={false}
            tabController={
              <DynamicTabController
                colours={colours}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
              >
                <TabPanel label="Clinic" visible={true}>
                  <TabContent>
                    <Heading>Clinic Details</Heading>
                    <Paragraph>Please provide details for the clinic, required fields are indicated by an asterisk <span style={{ color: '#880808' }}> *</span></Paragraph>
                    {scheduleInUse && <Alert type='warning'>The <b>Date of Clinic</b>, <b>Duration</b>, <b>Start Time</b> and <b>End Time</b> fields have been locked because schedules have already been created for this clinic appointment. You will need to cancel these schedules before you can make changes.</Alert>}
                    <ScheduleWrapper>
                      <Row>
                        <FieldContainer>
                          <DatePicker
                            label="Date of Clinic"
                            required={true}
                            missing={missingFields?.clinicdate}
                            value={clinicDetails?.clinicdate}
                            onChange={e => { handleChange('clinicdate', e.toISOString()); }}
                            disabled={scheduleInUse} />
                        </FieldContainer>

                        <FieldContainer>
                          <SelectWithLabel
                            label="Appointment Duration (minutes)"
                            options={appointmentDurations}
                            required={true}
                            missing={missingFields?.appointment_duration}
                            value={clinicDetails?.appointment_duration}
                            onChange={e => handleChange('appointment_duration', e.target.value)}
                            disabled={scheduleInUse} />
                        </FieldContainer>
                      </Row>

                      <Row>
                        <FieldContainer style={{ display: 'flex' }}>
                          <SelectWithLabel
                            label="Optician (this list is pulled from your selected favourite opticians)"
                            options={favouriteOpticians}
                            value={clinicDetails?.OpticianID}
                            missing={missingFields?.OpticianID}
                            required={true}
                            onChange={e => handleChange('OpticianID', e.target.value)}
                            disabled={scheduleInUse} />

                          <Button style={{ height: "40px", marginTop: "21px", marginLeft: "5px" }} onClick={ShowHideOptician}>{<AddPerson />}</Button>
                        </FieldContainer>

                        <FieldContainer>
                          <SelectWithLabel
                            label="Open Clinic"
                            value={clinicDetails?.OpenClinic}
                            missing={missingFields?.OpenClinic}
                            options={yesNo}
                            onChange={e => handleChange('OpenClinic', e.target.value)}
                            disabled={true} />
                        </FieldContainer>
                      </Row>

                      <Row>
                        <FieldContainer>
                          <SelectWithLabel
                            label="Start Time"
                            value={extractHHMM(clinicDetails?.starttime, true)}
                            options={times}
                            required={true}
                            missing={missingFields?.starttime}
                            onChange={e => handleChange('starttime', e.target.value)}
                            disabled={scheduleInUse} />
                        </FieldContainer>

                        <FieldContainer>
                          <SelectWithLabel
                            label="End Time"
                            value={extractHHMM(clinicDetails?.endtime, true)}
                            options={times}
                            required={true}
                            missing={missingFields?.endtime}
                            onChange={e => handleChange('endtime', e.target.value)}
                            disabled={scheduleInUse} />
                        </FieldContainer>
                      </Row>
                      <Row>
                        <FieldContainer>
                          <SelectWithLabel
                            label="Account"
                            options={linkedAccounts}
                            required={true}
                            value={clinicDetails?.accountid}
                            onChange={(e) => handleChange('accountid', e.target.value)}
                            missing={missingFields?.accountid}
                            disabled={scheduleInUse}
                          />
                        </FieldContainer>
                      </Row>
                    </ScheduleWrapper>
                  </TabContent>
                </TabPanel>
                <TabPanel label="Schedule" visible={!isNewClinic}>
                  <TabContent>
                    <Heading>Clinic Schedule</Heading>
                    <Paragraph>Book EyeTicket appointments with the selected optician.</Paragraph>
                    <Alert type='info'>To cancel appointments, click the red <b>'x'</b> button</Alert>
                    <br></br>
                    <ScheduleWrapper>
                      <Schedule
                        events={clinicSchedule}
                        startTime={extractHHMM(clinicDetails?.starttime, true)}
                        endTime={extractHHMM(clinicDetails?.endtime, true)}
                        interval={clinicDetails?.appointment_duration}
                        buttonFn={ShowHideEyeTicket}
                        selectedSchedule={setSelectedScheduleData}
                        onDeleteEyeticket={handleRemoveEyeTicket}
                        onDeleteRestriction={handleRemoveRestriction}
                        onAddRestriction={handleAddRestriction}
                        isLoading={isLoading}
                      />
                    </ScheduleWrapper>
                  </TabContent>
                </TabPanel>
              </DynamicTabController>
            }
          >

          </TabbedCard>
          <ModalFooter>
            {isLoading && <Alert type='information'>Loading..</Alert>}
            {Object.values(missingFields).some(isMissing => isMissing) &&
              (warningMessage ?
                <Alert type='warning'>{warningMessage}</Alert>
                :
                <Alert type='warning'>Please populate all required fields indicated by an asterisk <span style={{ color: '#880808' }}> *</span></Alert>)
            }
            {error && !isLoading && <Alert type='error'>Unable to process request due to <b>{error.message}</b></Alert>}
            <CancelButton onClick={showHideModal}>Cancel</CancelButton>
            <Button
              onClick={handleSaveChanges}
              disabled={scheduleInUse || isLoading}
              title={(scheduleInUse ? "You cannot make changes to a schedule that has EyeTickets associated to it." : "")}>✓ Save Changes</Button>
          </ModalFooter>
        </StyledModal>
      </Modal>
    </>
  );
}
